import React, { memo, useCallback, useMemo } from "react";

import { PC_HALFCARD_STATES } from "@swiggy-private/cart-types";

import {
    TOING_PRICE_SYSTEM_ERROR_CONSTANTS,
    TOING_PRICE_IMAGE_TOO_LARGE_ERROR_CONSTANTS,
} from "./constants";
import { CardContainer } from "./styles";
import type { IToingPriceComparisonHalfCardProps } from "./types";
import ErrorStateHalfCard from "./component/ErrorStateHalfCard";
import InstructionState from "./component/InstructionState";
import ScanningState from "./component/ScanningState";
import SuccessLottie from "./component/SuccessLottie";

const PriceComparisonHalfCard: React.FC<IToingPriceComparisonHalfCardProps> = ({
    comparisonState,
    priceComparisonData,
    totalSavings,
    onImpression,
    onResultImpression,
    onScanningImpression,
}) => {
    const isErrorState = comparisonState === PC_HALFCARD_STATES.ERROR;

    const renderInstructionState = useCallback(() => {
        return <InstructionState instructionsData={priceComparisonData?.instructions_data} onImpression={onImpression} />;
    }, [priceComparisonData?.instructions_data, onImpression]);

    const renderScanningState = useCallback(
        (shouldFireImpression: boolean) => {
            return (
                <ScanningState
                    scanningData={priceComparisonData?.scanning_states_data}
                    onImpression={shouldFireImpression ? onScanningImpression : undefined}
                />
            );
        },
        [priceComparisonData?.scanning_states_data, onScanningImpression],
    );

    const renderSuccessState = useCallback(() => {
        return (
            <SuccessLottie
                successData={priceComparisonData?.success_state_halfcard_data}
                rejectionData={priceComparisonData?.rejection_state_halfcard_data}
                totalSavings={totalSavings}
                onImpression={onResultImpression}
            />
        );
    }, [
        priceComparisonData?.success_state_halfcard_data,
        priceComparisonData?.rejection_state_halfcard_data,
        totalSavings,
        onResultImpression,
    ]);

    const content = useMemo(() => {
        switch (comparisonState) {
            case PC_HALFCARD_STATES.INSTRUCTIONS:
                return renderInstructionState();
            case PC_HALFCARD_STATES.UPLOAD_IMAGE:
                return renderScanningState(false);
            case PC_HALFCARD_STATES.SCANNING:
                return renderScanningState(true);
            case PC_HALFCARD_STATES.SUCCESS:
                return renderSuccessState();
            case PC_HALFCARD_STATES.ERROR:
                return (
                    <ErrorStateHalfCard
                        icon={TOING_PRICE_SYSTEM_ERROR_CONSTANTS.icon}
                        title={TOING_PRICE_SYSTEM_ERROR_CONSTANTS.title}
                        subtitle={TOING_PRICE_SYSTEM_ERROR_CONSTANTS.subtitle}
                        errorImage={TOING_PRICE_SYSTEM_ERROR_CONSTANTS.errorImage}
                    />
                );
            case PC_HALFCARD_STATES.IMAGE_TOO_LARGE_ERROR:
                return (
                    <ErrorStateHalfCard
                        icon={TOING_PRICE_IMAGE_TOO_LARGE_ERROR_CONSTANTS.icon}
                        title={TOING_PRICE_IMAGE_TOO_LARGE_ERROR_CONSTANTS.title}
                        subtitle={TOING_PRICE_IMAGE_TOO_LARGE_ERROR_CONSTANTS.subtitle}
                        errorImage={TOING_PRICE_IMAGE_TOO_LARGE_ERROR_CONSTANTS.errorImage}
                    />
                );
            default:
                return renderInstructionState();
        }
    }, [comparisonState, renderInstructionState, renderScanningState, renderSuccessState]);

    return <CardContainer $isError={isErrorState}>{content}</CardContainer>;
};

export default memo(PriceComparisonHalfCard);
